import React, { useRef, useEffect, useState, useMemo } from 'react';

import {
  TextField,
  FormControl,
  FormHelperText,
  Chip,
} from '@material-ui/core';
import DoneAllRoundedIcon from '@material-ui/icons/DoneAllRounded';
import PlaylistAddCheckRoundedIcon from '@material-ui/icons/PlaylistAddCheckRounded';
import { Autocomplete as AutocompleteUI } from '@material-ui/lab';
import { useField } from '@unform/core';
import { isEmpty, find, isEqual, isFunction } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';

import { Hidden } from '~/components/Form';
import { t, canURL } from '~/helpers';
import api from '~/services/api';

function Autocomplete({
  name,
  label,
  title,
  getValue,
  serialize,
  url,
  params,
  unique,
  hiddens,
  first,
  disabled,
  excluded,
  variant,
  onSerialize,
  isGrid,
  options,
  onChangeValueExpecific,
  filteruser,
  ...rest
}) {
  const inputRef = useRef(null);
  const permission = isEqual(canURL(), { read: true });

  const {
    fieldName,
    defaultValue,
    registerField,
    error,
    clearError,
  } = useField(name);

  const [data, setData] = useState([]);
  const [value, setValue] = useState([]);
  const [fieldHidden, setFieldHidden] = useState(hiddens);
  const [loading, setLoading] = useState(false);
  const [oldParams, setOldParams] = useState({});

  // Render items field hidden
  const renderItemsHidden = useMemo(
    () =>
      fieldHidden.map((fh) => (
        <Hidden name={fh.name} key={fh.name} value={fh.value || ''} />
      )),
    [fieldHidden]
  );

  function handleChangeValue(newValue) {
    const defValue = { [label]: null, [unique]: null };

    if (onChangeValueExpecific) {
      onChangeValueExpecific();
    }

    inputRef.current.value = serialize(newValue ? newValue[unique] : newValue);
    setValue(newValue);
    getValue(serialize(newValue || defValue));

    // Check exists field hidden
    if (!isEmpty(fieldHidden) && newValue.length > 0) {
      const newHiddens = hiddens?.map((nh) => ({
        ...nh,
        value: newValue[0][nh?.name],
      }));

      setFieldHidden(newHiddens);
    } else {
      setFieldHidden(hiddens);
    }
  }

  async function handleData() {
    try {
      setLoading(true);
      setValue([]);

      let modItems;
      let response;

      if (options != null) {
        response = options;
      } else {
        const responseFetch = await api.get(url, {
          params: {
            ...params,
            pagesize: 99999,
            pesquisalista: true,
            filteruser,
          },
          paramsSerializer: (param) => qs.stringify(param),
        });
        response = responseFetch.data;
      }

      const { items } = response;

      if (!isEmpty(response)) {
        if (isFunction(onSerialize)) {
          modItems = [...first, ...onSerialize(response)].filter(
            (mod) => !excluded.includes(mod[unique])
          );
        } else {
          modItems = [...first, ...items].filter(
            (mod) => !excluded.includes(mod[unique])
          );
        }

        setData(modItems);
        setOldParams(params);
      }

      if (defaultValue != null && defaultValue != undefined) {
        const findItem = find(items, (i) => i[unique] === defaultValue);

        setValue(findItem);
        getValue(serialize(findItem));
      }
    } catch (err) {
      // console.tron.log(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [registerField, fieldName]);

  useEffect(() => {
    handleData();
  }, [filteruser]);

  useEffect(() => {
    if (!isEqual(params, oldParams) && !loading && !isEmpty(oldParams)) {
      handleData();
    }
  }, [url, params]);

  useEffect(() => {
    inputRef.current.value = defaultValue;
  }, [defaultValue]);

  useEffect(() => {
    if (disabled) clearError();
  }, [disabled]);

  return (
    <FormControl style={{ width: '100%' }}>
      <AutocompleteUI
        popupIcon={<DoneAllRoundedIcon />}
        getOptionLabel={(option) => t(option[label].toString())}
        {...rest}
        size="small"
        options={data}
        value={value}
        onChange={(e, newValue) => handleChangeValue(newValue)}
        disabled={(permission || disabled) && !isGrid}
        onFocus={clearError}
        renderInput={(props) => (
          <TextField
            ref={inputRef}
            error={!!error}
            label={title}
            variant={variant}
            fullWidth
            {...props}
            disabled={(permission || disabled) && !isGrid}
          />
        )}
      />

      {error && <FormHelperText error>{error}</FormHelperText>}
      {renderItemsHidden}
    </FormControl>
  );
}

Autocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  getValue: PropTypes.func,
  serialize: PropTypes.func,
  url: PropTypes.string.isRequired,
  params: PropTypes.objectOf(PropTypes.any),
  unique: PropTypes.string.isRequired,
  hiddens: PropTypes.arrayOf(PropTypes.any),
  first: PropTypes.arrayOf(PropTypes.any),
  disabled: PropTypes.bool,
  excluded: PropTypes.arrayOf(PropTypes.any),
  variant: PropTypes.string,
  onSerialize: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  isGrid: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.any),
  onChangeValueExpecific: PropTypes.func,
  filteruser: PropTypes.bool,
};

Autocomplete.defaultProps = {
  getValue: () => {},
  serialize: (value) => value,
  params: {},
  hiddens: [],
  first: [],
  disabled: false,
  excluded: [],
  variant: 'outlined',
  onSerialize: false,
  isGrid: false,
  onChangeValueExpecific: () => {},
  options: null,
  filteruser: true,
};

export default Autocomplete;
